// Mercapoli - Proximity Alert Component
// Shows a prominent alert when rider is nearby
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bike, MapPin, X, Clock } from 'lucide-react';
import { Button } from './ui/button';

const ProximityAlert = ({ 
  isVisible, 
  riderName = "Tu repartidor", 
  distanceM = 500, 
  estimatedMinutes = 2,
  onClose 
}) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -100, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className="fixed top-4 left-4 right-4 z-[100] md:left-auto md:right-4 md:w-96"
          data-testid="proximity-alert"
        >
          <div className="bg-gradient-to-r from-cyan-500 to-teal-500 rounded-2xl shadow-2xl overflow-hidden">
            {/* Animated background */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,white,transparent_50%)] animate-pulse" />
            </div>
            
            <div className="relative p-4">
              <div className="flex items-start gap-4">
                {/* Animated icon */}
                <motion.div
                  animate={{ 
                    x: [0, 5, 0, -5, 0],
                    rotate: [0, 5, 0, -5, 0]
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="flex-shrink-0 w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center"
                >
                  <Bike className="h-8 w-8 text-white" />
                </motion.div>
                
                <div className="flex-1 text-white">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    🚴 ¡Tu pedido está cerca!
                  </h3>
                  <p className="text-white/90 text-sm mt-1">
                    {riderName} está a menos de {Math.round(distanceM)}m de tu ubicación
                  </p>
                  
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-1.5 text-white/80 text-sm">
                      <MapPin className="h-4 w-4" />
                      <span>{Math.round(distanceM)}m</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-white/80 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>~{estimatedMinutes} min</span>
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="flex-shrink-0 text-white/70 hover:text-white hover:bg-white/10"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Progress bar animation */}
              <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 10, ease: "linear" }}
                  className="h-full bg-white/60 rounded-full"
                />
              </div>
              <p className="text-center text-white/60 text-xs mt-2">
                Prepárate para recibir tu pedido
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProximityAlert;
